import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatMonthYearText } from "@/utils/shared/dateUtils";
import { Star, X } from "lucide-react";
import { useState } from "react";

interface Review {
  id: string;
  name: string;
  rating: number;
  comment: string;
  location: string;
  date: string;
}

interface ReviewsSectionProps {
  reviews: Review[];
}

const ReviewsSection = ({ reviews }: ReviewsSectionProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const displayedReviews = reviews.slice(0, 4);
  const totalReviews = reviews.length;

  const truncateComment = (comment: string, maxLength: number = 200) => {
    if (comment.length <= maxLength) {
      return comment;
    }
    return comment.substring(0, maxLength) + "...";
  };

  const ReviewCard = ({ review }: { review: Review }) => (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow flex-shrink-0 w-80 md:w-auto md:flex-shrink h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="font-semibold text-gray-900">{review.name}</p>
          <p className="text-sm text-gray-500">{review.location}</p>
        </div>
        <div className="flex items-center space-x-1">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className={`w-4 h-4 ${
                i < review.rating
                  ? "fill-yellow-400 text-yellow-400"
                  : "text-gray-300"
              }`}
            />
          ))}
        </div>
      </div>
      <p className="text-gray-700 mb-3 leading-snug flex-grow">
        "{truncateComment(review.comment)}"
      </p>
      <p className="text-sm text-gray-500 mt-auto">
        {formatMonthYearText(review.date)}
      </p>
    </div>
  );

  return (
    <>
      <div className="py-6 border-b border-gray-200">
        <h3 className="text-xl font-semibold mb-6">Comentarios</h3>

        <div className="hidden md:grid md:grid-cols-2 gap-6 mb-6">
          {displayedReviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>

        <div className="md:hidden mb-4">
          <div className="flex space-x-4 overflow-x-auto pb-4 scrollbar-hide touch-pan-x">
            {displayedReviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>

          {displayedReviews.length > 1 && (
            <div className="flex justify-center space-x-2 mt-4">
              {displayedReviews.map((_, index) => (
                <div key={index} className="w-2 h-2 rounded-full bg-gray-300" />
              ))}
            </div>
          )}
        </div>

        {totalReviews > 4 && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="w-full md:w-auto px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
          >
            Mostrar {totalReviews} evaluaciones
          </button>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl h-[100vh] md:max-h-[80vh] md:h-auto p-0 m-0 md:m-auto rounded-none md:rounded-lg">
          <DialogTitle className="sr-only">Todas las evaluaciones</DialogTitle>
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-semibold text-gray-900">
                Evaluaciones
              </h2>
              <DialogClose className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                <X className="h-5 w-5" />
              </DialogClose>
            </div>
          </div>

          <ScrollArea className="flex-1 p-6">
            <div className="space-y-6">
              {reviews.map((review) => (
                <div
                  key={review.id}
                  className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {review.name}
                      </p>
                      <p className="text-sm text-gray-500">{review.location}</p>
                    </div>
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < review.rating
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-gray-300"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-gray-700 mb-3 leading-snug">
                    "{review.comment}"
                  </p>
                  <p className="text-sm text-gray-500">
                    {formatMonthYearText(review.date)}
                  </p>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReviewsSection;
